/**
 * What to tell the user when an AI action in the note form fails.
 *
 * Maps the server's stable error codes (NoteAiController) to three outcomes
 * the form can act on:
 *
 *  - not-configured: the practice has no AI set up (422 AI_NOT_CONFIGURED).
 *    The form shows a pop-up sending the user to their administrator.
 *  - rate-limited:   429 RATE_LIMITED.
 *  - failed:         anything else, with wording that says what to do.
 *
 * Server text is shown only where the server wrote it for users (the AI and
 * rate-limit codes), never for validation or an unexpected 5xx, whose
 * messages name fields or carry internals.
 */
import { ApiRequestError } from "@/v5/lib/api";

export type NoteAiErrorKind = "not-configured" | "rate-limited" | "failed";

export interface NoteAiErrorDescription {
  kind: NoteAiErrorKind;
  message: string;
}

export const AI_NOT_CONFIGURED_MESSAGE =
  "AI isn't set up for your practice yet. Ask your account administrator to set it up in the Admin area.";

const RATE_LIMITED_FALLBACK = "Too many tries in a short time. Wait a minute and try again.";

const TOO_LONG_MESSAGE = "The text is too long for AI to work with. Shorten it and try again.";

const FAILED_FALLBACK =
  "That couldn't be done just now. Try again in a minute, and contact support if it keeps happening.";

/** The server's own sentence, without the `[status]` prefix api.ts adds. */
function serverText(err: ApiRequestError): string {
  const text = err.message.replace(/^\[\d{3}\]\s*/, "").trim();
  return text === "API error" ? "" : text;
}

export function describeNoteAiError(err: unknown): NoteAiErrorDescription {
  if (!(err instanceof ApiRequestError)) {
    return { kind: "failed", message: FAILED_FALLBACK };
  }

  if (err.code === "AI_NOT_CONFIGURED") {
    return { kind: "not-configured", message: AI_NOT_CONFIGURED_MESSAGE };
  }

  if (err.status === 429 || err.code === "RATE_LIMITED") {
    return { kind: "rate-limited", message: serverText(err) || RATE_LIMITED_FALLBACK };
  }

  if (err.code === "BEDROCK_ERROR" || err.code === "AI_ACCESS_DENIED") {
    return { kind: "failed", message: serverText(err) || FAILED_FALLBACK };
  }

  // A bare 422 is request validation; in practice the transcript or prompt
  // is over the server's length limit. Its text names form fields, so it is
  // not shown.
  if (err.status === 422) {
    return { kind: "failed", message: TOO_LONG_MESSAGE };
  }

  return { kind: "failed", message: FAILED_FALLBACK };
}
